import { Icon } from '@iconify/react';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Card, Collapse, Divider, Grid, Stack, TextField, Typography } from '@mui/material';
import { t } from 'i18next';
import { isEmpty, toNumber } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import CreateSendingConfig from '../../components/onBoarding/CreateSendingConfig';
import SendMessage from '../../components/onBoarding/SendMessage';
import WhatsappGroupConfig, { ARIONEO_SUUPORT_NUMBER } from '../../components/onBoarding/WhatsappGroupConfig';
import { useAuth } from '../../hooks/useAuth';
import { useOnboarding } from '../../hooks/useOnboarding';
import { IHbDeal } from '../../shared/types/HbDeal.type';


interface CardData {
    title: string;
    icon: string;
    iconColor: string;
    component: React.ReactNode;
}



function Onboarding() {
    const { saveGroupChannel, getLatestHubspotId } = useOnboarding()
    const { auth, isAdmin } = useAuth();
    const { getDealId } = useOnboarding();
    const [dealId, setDealId] = useState<null | string>((isAdmin && getLatestHubspotId) ? getLatestHubspotId : null);
    const [hbDealInfo, setHbDealInfo] = useState<IHbDeal | null>(null);
    const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
    const [selectedDeals, setSelectedDeals] = useState<string[]>([]);
    const [selectedArioneo, setSelectedArioneo] = useState<string[]>([ARIONEO_SUUPORT_NUMBER]);
    const [groupName, setGroupName] = useState(`EQUIMETRE - ${auth?.full_name || ""}`);
    const [pendingChannels, setPendingChannels] = useState<string[]>([])
    const isInitialRender = useRef(true)


    useEffect(() => {
        if(!isInitialRender.current) return

        fetchDealId()
        isInitialRender.current = false

    }, [dealId])

    const fetchDealId = async () => {
        if (!dealId || !auth) return;
        try {
            const response = await getDealId.mutateAsync({
                userId: auth.id,
                dealId: toNumber(dealId),
            });
            setHbDealInfo(response.data);
        } catch (error) {
        }
    };




    const createCommunicationChannel = async (uuid: string, channel: string) => {
        if (uuid) {
            await fetchDealId()
        } else {
            setPendingChannels(prev => [...prev, channel])
        }

        setSelectedArioneo([])
        setSelectedDeals([])
        fetchDealId()

    }

    const isUserHasAlreadyGroup = useMemo(() => {
        return hbDealInfo?.communication_channels.some(channel => channel.type === 'WHATSAPP_GROUP')
    }, [hbDealInfo?.user?.id])

    const cards: CardData[] = [
        {
            title: t('onboarding:createWaGroup'),
            icon: 'material-symbols:group-add-rounded',
            iconColor: '#25D366',
            component: <WhatsappGroupConfig
                selectedDeals={selectedDeals}
                selectedArioneo={selectedArioneo}
                groupName={groupName}
                setSelectedDeals={vals => setSelectedDeals(vals)}
                setSelectedArioneo={vals => setSelectedArioneo(vals)}
                setGroupName={vals => setGroupName(vals)}
                onGroupCreated={createCommunicationChannel}
                creatingChannel={saveGroupChannel.isLoading}
                isUserHasAlreadyGroup={isUserHasAlreadyGroup}
                hbData={hbDealInfo} />
        },
        {
            title: t('onboarding:createSentConfig'),
            icon: 'tabler:send',
            iconColor: '#FF9800',
            component: <CreateSendingConfig
                commChannels={hbDealInfo?.communication_channels || []}
                pendingChannels={pendingChannels}
                isUserHasTrainingConfig={!isEmpty(hbDealInfo?.send_training_configurations)}
                isUserHasGallopConfig={!isEmpty(hbDealInfo?.send_gallop_review_configurations)}
                isUserHasStableConfig={!isEmpty(hbDealInfo?.send_stable_review_configurations)}
            />
        },
        {
            title: t('onboarding:message'),
            icon: 'fluent-mdl2:onboarding',
            iconColor: '#3F51B5',
            component: <SendMessage
                commChannels={hbDealInfo?.communication_channels || []}
                templates={hbDealInfo?.whatsapp_message_templates || []} />
        },
    ];


    const hexToRgba = (hex: string, alpha = 0.1) => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    const handleToggle = (index: number) => {
        setExpandedIndex(prev => (prev === index ? null : index));
    };

    return (
        <Stack direction="column">
            <Box
                display="flex"
                gap={0}
                justifyContent="center"
                alignItems="center"
                sx={{
                    maxWidth: 420,
                    mx: "auto",
                    bgcolor: "background.paper",
                    borderRadius: 3,
                    boxShadow: "0px 2px 10px rgba(0,0,0,0.08)",
                    overflow: "hidden",
                }}
            >
                <TextField
                    placeholder={t('onboarding:dealIfField')}
                    type="number"
                    value={dealId}
                    onKeyDown={e => {
                        if (e.key === 'Enter') {
                            fetchDealId()
                        }
                    }}
                    autoFocus
                    onChange={(e) => setDealId(e.target.value)}
                    variant="outlined"
                    size="small"
                    fullWidth
                    InputProps={{
                        sx: {
                            borderRadius: 0,
                            "& fieldset": { border: "none" }, // remove default MUI border
                            fontSize: 15,
                            px: 2,
                        },
                    }}
                />

                <LoadingButton
                    loading={getDealId.isLoading}
                    onClick={fetchDealId}
                    disabled={!dealId}
                    variant="contained"
                    sx={{
                        borderRadius: 0,
                        px: 3,
                        fontWeight: 500,
                        fontSize: 15,
                        bgcolor: "primary.main",
                        "&:hover": { bgcolor: "primary.dark" },
                        textTransform: "none",
                    }}
                >
                    {t('equimetre:search')}
                </LoadingButton>
            </Box>

            {hbDealInfo && (
                <>
                    <Divider sx={{ my: 2 }} />
                    <Box mb={2} display={'flex'} justifyContent={'space-between'} alignItems={'center'}
                        flexWrap={'wrap'}>
                        <Typography variant='h5'
                            mb={1}>{t('onboarding:heading', { user: hbDealInfo.user?.full_name })} </Typography>
                        <Button size='small' onClick={() => {
                            setHbDealInfo(null)
                            setDealId('')
                        }} variant='outlined' startIcon={<Icon
                            icon={'material-symbols:clear-all-rounded'} />}>{t('button:reset')}</Button>
                    </Box>
                    <Grid container spacing={2}>
                        {cards.map((card, index) => (
                            <Grid item md={12} sm={12} xs={12} key={index}>
                                <Card sx={{ overflow: 'hidden' }}>
                                    {/* Header */}
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        gap={2}
                                        sx={{ cursor: 'pointer', p: 2 }}
                                        onClick={() => handleToggle(index)}
                                    >
                                        <Card
                                            sx={{
                                                backgroundColor: hexToRgba(card.iconColor, 0.1),
                                                padding: 1.5,
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                minWidth: 50,
                                                minHeight: 50,
                                                flexShrink: 0,
                                            }}
                                        >
                                            <Icon icon={card.icon} width={28} height={28} color={card.iconColor} />
                                        </Card>
                                        <Typography variant="subtitle2" sx={{ fontSize: 14, flexGrow: 1 }}>
                                            {card.title}
                                        </Typography>

                                        {/* Accordion toggle icon */}
                                        <Icon
                                            icon="material-symbols:expand-more-rounded"
                                            width={24}
                                            height={24}
                                            style={{
                                                transform: expandedIndex === index ? 'rotate(180deg)' : 'rotate(0deg)',
                                                transition: 'transform 0.3s',
                                            }}
                                        />
                                    </Box>


                                    {/* Collapse content */}
                                    <Collapse in={expandedIndex === index} timeout="auto" unmountOnExit>
                                        <Box sx={{ p: 2 }}>
                                            <Divider sx={{ mb: 2 }} />
                                            {card.component}
                                        </Box>
                                    </Collapse>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </>
            )}
        </Stack>
    );
}

export default Onboarding;
